import * as React from 'react';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';

import { NewBreadcrumb } from '../../../components/synced/ui/NewBreadcrumb/NewBreadcrumb';
import { alertWarn } from '../../../store/reducers/Alert';
import { services } from '../../../services';
import { useBackgroundPromiseWrapper } from '../../../helpers/hooks/useBackgroundPromiseWrapper';
import { useDownload } from '../../../helpers/hooks/useDownload';
import { useIsMobile } from '../../../helpers/hooks/useIsMobile';
import { ReportFilterBar } from '../../reports/components/ReportFilterBar';
import { ReportPageHeader } from '../../reports/components/ReportPageHeader';
import { useReportFilters } from '../../reports/components/useReportFilters';
import { STATUS_TITLE } from '../../reports/components/statusTitles';
import styles from './ProjectConsolidated.module.css';

const STATUS_STYLE: Record<string, { dot: string; color: string }> = {
    [STATUS_TITLE.TOTAL]: { dot: '#aeaeb2', color: 'var(--text-primary)' },
    [STATUS_TITLE.NOT_SUBM]: { dot: '#e0342b', color: 'var(--danger-color)' },
    [STATUS_TITLE.SUBM]: { dot: '#f5d90a', color: '#a67c00' },
    [STATUS_TITLE.CHECKING]: { dot: '#0a84ff', color: 'var(--accent-blue)' },
    [STATUS_TITLE.IN_WORK]: { dot: '#f5a623', color: 'var(--accent-orange)' },
    [STATUS_TITLE.APPROVED]: { dot: '#2fa35c', color: '#1c8a4d' },
    [STATUS_TITLE.APPR_REM]: { dot: '#7cc79a', color: '#1c8a4d' },
    [STATUS_TITLE.GIP]: { dot: '#b0b4b8', color: 'var(--text-muted)' },
    [STATUS_TITLE.REVOKED]: { dot: '#e0342b', color: 'var(--danger-color)' }
};

const STATUS_PILL: Record<string, { bg: string; text: string; dot: string }> = {
    [STATUS_TITLE.APPROVED]: { bg: '#e9f6ee', text: '#1c8a4d', dot: '#2fa35c' },
    [STATUS_TITLE.APPR_REM]: { bg: '#e9f6ee', text: '#1c8a4d', dot: '#7cc79a' },
    [STATUS_TITLE.CHECKING]: { bg: '#eaf3ff', text: '#0a6ff0', dot: '#0a84ff' },
    [STATUS_TITLE.IN_WORK]: { bg: '#fff6e5', text: '#a85c00', dot: '#f5a623' },
    [STATUS_TITLE.NOT_SUBM]: { bg: '#fff0ef', text: '#c0392b', dot: '#e0342b' },
    [STATUS_TITLE.GIP]: { bg: '#f5f5f7', text: '#6e6e73', dot: '#b0b4b8' }
};

type DeviationSign = '•' | '♦' | '▲' | string;

interface TableRow {
    Cypher: string;
    Parents: string;
    Title: string;
    StatusTitle: string;
    CompletionPercentage: string | number;
    ContractDate: string;
    WFInstanceCount: string | number;
    LastRevisionModified: string;
    DeviationContractDateSign: DeviationSign;
    DeviationContractDate: string;
    DueDateExpertTask: string;
    CompletedDateExpertTask: string;
    ExceedingDayExpertTask: string | number;
    DueDateGIPTask: string;
    CompletedDateGIPTask: string;
    ExceedingDayGIPTask: string | number;
    ApprovalPeriodTaskSign: DeviationSign;
    ApprovalPeriodTask: string;
}

function StatusPill({ title }: { title: string }) {
    const s = STATUS_PILL[title];
    if (!s) return <span>{title}</span>;
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 10px',
                borderRadius: 980,
                fontSize: 11.5,
                fontWeight: 600,
                background: s.bg,
                color: s.text,
                whiteSpace: 'nowrap'
            }}
        >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
            {title}
        </span>
    );
}

function DeviationCell({ sign, value }: { sign: DeviationSign; value: string }) {
    const color =
        sign === '•'
            ? 'var(--success-color)'
            : sign === '♦'
              ? 'var(--danger-color)'
              : sign === '▲'
                ? 'var(--accent-orange)'
                : undefined;

    return (
        <span className={styles.deviationCell}>
            {color && (
                <span className={styles.deviationSign} style={{ color }}>
                    {sign}
                </span>
            )}
            {value}
        </span>
    );
}

function ProgressBar({ value, label }: { value: number; label: string }) {
    const pct = Math.min(100, Math.max(0, value));
    return (
        <div className={styles.progressBlock}>
            <div className={styles.progressLabel}>
                <span>{label}</span>
                <span className={styles.progressPct}>{pct}%</span>
            </div>
            <div className={styles.progressTrack}>
                <div className={styles.progressFill} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

export const ProjectConsolidated: React.FC = () => {
    const dispatch = useDispatch();
    const filters = useReportFilters();
    const [promiseWrapper, isLoading] = useBackgroundPromiseWrapper();
    const { downloadFile, isDownloading } = useDownload();
    const [data, setData] = React.useState<any>();
    const isMobile = useIsMobile(900);

    React.useEffect(() => {
        document.body.style.background = 'var(--bg-page)';
        return () => {
            document.body.style.background = '';
        };
    }, []);

    const breadCrumbItems = [{ label: 'Отчёты', to: '/reports' }, { label: 'Сводный отчёт по ПД и РД' }];

    const viewReport = () => {
        if (!filters.selectedProjectDoc) {
            dispatch(alertWarn({ detail: 'Необходимо выбрать документацию' }));
            return;
        }
        promiseWrapper(
            () => services.projectDocService.projectConsolidated(filters.selectedProjectDoc!.Id ?? 0),
            (res: any) => setData(res)
        );
    };

    const downloadReport = () => {
        const id = filters.selectedProjectDoc?.Id;
        if (id) void downloadFile(`ProjectDocs/ProjectConsolidatedDownload?id=${id}`);
    };

    const statusMap = Object.fromEntries(
        ((data?.Statuses ?? []) as { Title: string; Value: number }[]).map((s) => [s.Title, s.Value])
    );
    const statuses = [...(data?.Statuses ?? ([] as { Title: string; Value: number }[]))]
        .reverse()
        .filter((s: { Title: string }) => s.Title in STATUS_PILL);
    const total = statusMap['ВСЕГО'] ?? 0;
    const pctDev = statusMap['Процент разработки'] ?? 0;
    const pctAppr = statusMap['Процент согласования'] ?? 0;
    const allApproved =
        total > 0 && (statusMap[STATUS_TITLE.APPROVED] ?? 0) + (statusMap[STATUS_TITLE.APPR_REM] ?? 0) >= total;

    const rows: TableRow[] = data?.Table1?.Values ?? [];

    return (
        <div className={clsx(styles.page, 'project-consolidated-page')}>
            <NewBreadcrumb items={breadCrumbItems} grayAncestors />

            <ReportFilterBar {...filters} isLoading={isLoading} isDownloading={isDownloading} onView={viewReport} onDownload={downloadReport} />

            {data && (
                <div className={styles.report}>
                    <ReportPageHeader title={data.Name} date={data.Date} />

                    <div className={styles.summaryCard}>
                        <div className={styles.summaryLeft}>
                            <span className={styles.totalLabel}>Всего томов</span>
                            <span className={styles.totalCount}>{total}</span>
                            {allApproved && (
                                <span className={styles.allApprovedBadge}>
                                    <i className="fa-solid fa-circle-check" /> Все согласованы
                                </span>
                            )}
                        </div>

                        <div className={styles.summaryCenter}>
                            {statuses.map((s: { Title: string; Value: number }) => {
                                const val = s.Value ?? 0;
                                const st = STATUS_STYLE[s.Title];
                                return (
                                    <div key={s.Title} className={styles.summaryStatusRow}>
                                        <span className={styles.summaryLeft_inner}>
                                            <span
                                                className={styles.summaryDot}
                                                style={{ background: st?.dot ?? 'var(--text-muted)' }}
                                            />
                                            <span className={styles.summaryStatusLabel}>{s.Title}</span>
                                        </span>
                                        <span
                                            className={styles.summaryStatusCount}
                                            style={{
                                                color:
                                                    val > 0 ? (st?.color ?? 'var(--text-muted)') : 'var(--text-muted)'
                                            }}
                                        >
                                            {val}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Справа: прогресс-бары */}
                        <div className={styles.summaryRight}>
                            <ProgressBar value={pctDev} label="Процент разработки" />
                            <ProgressBar value={pctAppr} label="Процент согласования" />
                        </div>
                    </div>

                    {isMobile ? (
                        <div className={styles.mobileList}>
                            {rows.map((row, i) => (
                                <div key={i} className={styles.mobileRow}>
                                    <div className={styles.mobileRowTop}>
                                        <div className={styles.mobileRowTopLeft}>
                                            <div className={styles.mobileRowTopLine}>
                                                {row.Cypher && <span className={styles.mobileCode}>{row.Cypher}</span>}
                                                {row.StatusTitle && <StatusPill title={row.StatusTitle} />}
                                            </div>
                                            <div className={styles.mobileTitle}>{row.Title}</div>
                                        </div>
                                        {row.CompletionPercentage != null && (
                                            <span className={styles.mobilePct}>{row.CompletionPercentage}</span>
                                        )}
                                    </div>

                                    <div className={styles.mobileBody}>
                                        <div className={styles.mobileGrid}>
                                            <div className={styles.mobileGroup}>
                                                <div className={styles.mobileGroupTitle} style={{ color: '#1c8a4d' }}>
                                                    Эксперт
                                                </div>
                                                <div className={styles.mobileField}>
                                                    <span>План</span>
                                                    <span>{row.DueDateExpertTask || '—'}</span>
                                                </div>
                                                <div className={styles.mobileField}>
                                                    <span>Факт</span>
                                                    <span>{row.CompletedDateExpertTask || '—'}</span>
                                                </div>
                                            </div>
                                            <div className={styles.mobileGroup}>
                                                <div className={styles.mobileGroupTitle} style={{ color: '#2f5fb0' }}>
                                                    ГИП
                                                </div>
                                                <div className={styles.mobileField}>
                                                    <span>План</span>
                                                    <span>{row.DueDateGIPTask || '—'}</span>
                                                </div>
                                                <div className={styles.mobileField}>
                                                    <span>Факт</span>
                                                    <span>{row.CompletedDateGIPTask || '—'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.mobileFooter}>
                                        <span>
                                            Ревизий: <b>{row.WFInstanceCount ?? '—'}</b> · послед.{' '}
                                            {row.LastRevisionModified || '—'}
                                        </span>
                                        <span className={styles.mobileFooterDuration}>
                                            <DeviationCell
                                                sign={row.ApprovalPeriodTaskSign}
                                                value={row.ApprovalPeriodTask}
                                            />
                                        </span>
                                    </div>
                                </div>
                            ))}

                            {data.WFInstanceCountSum != null && (
                                <div className={styles.mobileRowTotal}>
                                    <span className={styles.mobileRowTotalLabel}>Итого</span>
                                    <span>
                                        Ревизий: <b>{data.WFInstanceCountSum}</b> · Согласовано:{' '}
                                        <b style={{ color: '#1c8a4d' }}>{statusMap[STATUS_TITLE.APPROVED] ?? 0}</b>
                                    </span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.tableWrapper}>
                            <div className={styles.tableHeader}>
                                {data.ReportName && (
                                    <div className={styles.tableHeaderTitle}>
                                        <div className={styles.tableHeaderSub}>
                                            <i className="fa-regular fa-building" />
                                            {data.ReportName}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div style={{ overflowX: 'auto' }}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th className={clsx(styles.th, 'text-nowrap')} rowSpan={3}>
                                                Шифр тома
                                            </th>
                                            <th className={styles.th} rowSpan={3}>
                                                Наименование раздела
                                            </th>
                                            <th className={styles.th} rowSpan={3}>
                                                Наименование тома
                                            </th>
                                            <th className={styles.th} rowSpan={3}>
                                                Статус
                                            </th>
                                            <th className={`${styles.th} ${styles.tdCenter}`} rowSpan={3}>
                                                % согл.
                                            </th>
                                            <th className={styles.thGroup} colSpan={4} rowSpan={2}>
                                                Сроки реализации проекта
                                            </th>
                                            <th className={`${styles.thGroup} ${styles.thApproval}`} colSpan={6}>
                                                Сроки согласования документации
                                            </th>
                                            <th className={`${styles.th} ${styles.tdCenter} ${styles.noBr}`} rowSpan={3}>
                                                Общая длитель&shy;ность
                                            </th>
                                        </tr>
                                        <tr>
                                            <th className={`${styles.thGroup} ${styles.thExpert}`} colSpan={3}>
                                                Эксперт
                                            </th>
                                            <th className={`${styles.thGroup} ${styles.thGip}`} colSpan={3}>
                                                ГИП
                                            </th>
                                        </tr>
                                        <tr>
                                            <th className={clsx(styles.th, styles.tdCenter)}>Срок по договору</th>
                                            <th className={clsx(styles.th, styles.tdCenter)}>Кол-во ревизий</th>
                                            <th className={clsx(styles.th, styles.tdCenter)}>
                                                Дата послед&shy;ней ревизии
                                            </th>
                                            <th className={clsx(styles.th, styles.tdCenter)}>
                                                Откло&shy;нение от срока
                                            </th>
                                            <th className={clsx(styles.th, styles.tdCenter)}>Плановая дата</th>
                                            <th className={clsx(styles.th, styles.tdCenter)}>Фактич. дата</th>
                                            <th className={clsx(styles.th, styles.tdCenter)}>Превы&shy;шение сроков</th>
                                            <th className={clsx(styles.th, styles.tdCenter)}>Плановая дата</th>
                                            <th className={clsx(styles.th, styles.tdCenter)}>Фактич. дата</th>
                                            <th className={clsx(styles.th, styles.tdCenter)}>Превы&shy;шение сроков</th>
                                        </tr>
                                        <tr>
                                            {Array.from({ length: 16 }, (_, i) => (
                                                <th key={i} className={styles.thNum}>
                                                    {i + 1}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, i) => (
                                            <tr key={i} className={styles.tr}>
                                                <td className={`${styles.td} ${styles.tdCode} text-nowrap`}>{row.Cypher}</td>
                                                <td className={`${styles.td} ${styles.tdMuted}`}>{row.Parents}</td>
                                                <td className={styles.td}>{row.Title}</td>
                                                <td className={styles.td}>
                                                    <StatusPill title={row.StatusTitle} />
                                                </td>
                                                <td className={`${styles.td} ${styles.tdCenter} ${styles.tdGreen}`}>
                                                    {row.CompletionPercentage}
                                                </td>
                                                <td className={`${styles.td} ${styles.tdRight}`}>{row.ContractDate}</td>
                                                <td className={`${styles.td} ${styles.tdCenter} ${styles.tdBold}`}>
                                                    {row.WFInstanceCount}
                                                </td>
                                                <td className={`${styles.td} ${styles.tdRight}`}>
                                                    {row.LastRevisionModified}
                                                </td>
                                                <td className={`${styles.td} ${styles.tdCenter}`}>
                                                    <DeviationCell
                                                        sign={row.DeviationContractDateSign}
                                                        value={row.DeviationContractDate}
                                                    />
                                                </td>
                                                <td className={`${styles.td} ${styles.tdRight}`}>
                                                    {row.DueDateExpertTask}
                                                </td>
                                                <td className={`${styles.td} ${styles.tdRight}`}>
                                                    {row.CompletedDateExpertTask}
                                                </td>
                                                <td className={`${styles.td} ${styles.tdCenter}`}>
                                                    {row.ExceedingDayExpertTask}
                                                </td>
                                                <td className={`${styles.td} ${styles.tdRight}`}>
                                                    {row.DueDateGIPTask}
                                                </td>
                                                <td className={`${styles.td} ${styles.tdRight}`}>
                                                    {row.CompletedDateGIPTask}
                                                </td>
                                                <td className={`${styles.td} ${styles.tdCenter}`}>
                                                    {row.ExceedingDayGIPTask}
                                                </td>
                                                <td className={`${styles.td} ${styles.tdCenter}`}>
                                                    <DeviationCell
                                                        sign={row.ApprovalPeriodTaskSign}
                                                        value={row.ApprovalPeriodTask}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className={styles.trTotal}>
                                            <td className={styles.td} colSpan={5}></td>
                                            <td className={`${styles.td} ${styles.tdRight}`}></td>
                                            <td className={`${styles.td} ${styles.tdCenter} ${styles.tdBold}`}>
                                                {data.WFInstanceCountSum}
                                            </td>
                                            <td className={styles.td}></td>
                                            <td className={`${styles.td} ${styles.tdCenter} ${styles.tdBold}`}>
                                                {data.DeviationContractDateSum}
                                            </td>
                                            <td className={styles.td}></td>
                                            <td className={styles.td}></td>
                                            <td className={`${styles.td} ${styles.tdCenter} ${styles.tdBold}`}>
                                                {data.ExceedingDayExpertTaskSum}
                                            </td>
                                            <td className={styles.td}></td>
                                            <td className={styles.td}></td>
                                            <td className={`${styles.td} ${styles.tdCenter} ${styles.tdBold}`}>
                                                {data.ExceedingDayGipTaskSum}
                                            </td>
                                            <td
                                                className={`${styles.td} ${styles.tdCenter} ${styles.tdBold} ${styles.tdGreen}`}
                                            >
                                                {data.ApprovalPeriodSum}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
